// Client-side PDF generation for the contractor invoice. The PDF is the
// single ingestible document (what an AP tool like Relay/QBO parses); the
// email body is a human-readable reference. Same invoice number on both so a
// tool can never double-create.
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmt$ = (n) => {
  const v = parseFloat(n || 0);
  return v === Math.round(v)
    ? '$' + Math.round(v).toLocaleString()
    : '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const baseBasisLabel = (l) =>
  l.basis === 'hours' ? `${parseFloat(l.quantity).toFixed(2)} h x ${fmt$(l.rate_or_amount)}`
  : l.basis === 'days' ? `${l.quantity} day${l.quantity === 1 ? '' : 's'} x ${fmt$(l.rate_or_amount)}`
  : l.basis === 'project' ? 'Fixed project'
  : l.basis === 'reimbursement' ? (l.note || 'Expense reimbursement')
  : l.basis === 'hybrid' ? (l.note || 'Hybrid')
  : l.basis === 'percent' ? (l.note || '% of client rate')
  : 'Flat monthly';

// Time left out of pay is stated on the line, so the total is never a surprise.
const basisLabel = (l) => l.unpaid_note ? `${baseBasisLabel(l)} - ${l.unpaid_note}` : baseBasisLabel(l);

// Stable per-invoice number shared by the PDF, the email, and the filename.
// Reads invoice_number_prefix from issuer_snapshot (frozen at submit time),
// falls back to 'CINV' if the snapshot pre-dates the prefix feature.
export function contractorInvoiceNumber(stmt) {
  const iss = stmt.issuer_snapshot || {};
  const prefix = iss.invoice_number_prefix || 'CINV';
  return `${prefix}-${stmt.period}-${String(stmt.id).slice(0, 8)}`;
}

// Fetch a remote image URL and return { dataUri, w, h } with a hard timeout.
// Returns null on any failure — callers must handle null gracefully.
export async function fetchLogoForPdf(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUri = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    // Resolve natural image dimensions with a 3-second timeout.
    const dims = await Promise.race([
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve(null);
        img.src = dataUri;
      }),
      new Promise((resolve) => setTimeout(() => resolve(null), 3000)),
    ]);
    if (!dims || !dims.w || !dims.h) return null;
    return { dataUri, ...dims };
  } catch {
    return null;
  }
}

// Synchronous PDF builder. Logo is pre-fetched by the caller via fetchLogoForPdf
// and passed in as { dataUri, w, h } or null — this function never does I/O.
// Returns the PDF as a base64 string (no data-uri prefix), ready for Resend.
export function buildContractorInvoicePdfBase64(stmt, toName, logo) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const iss = stmt.issuer_snapshot || {};
  const periodLabel = new Date(stmt.period + '-02').toLocaleString('en-US', { month: 'long', year: 'numeric' });
  const invNo = contractorInvoiceNumber(stmt);

  // --- Header band (dark blue) ---
  const headerH = 72;
  doc.setFillColor(30, 58, 95);
  doc.rect(0, 0, pageW, headerH, 'F');

  // Logo (left side, aspect-ratio preserved)
  const logoMaxW = 140, logoMaxH = 44, logoPad = 28;
  if (logo) {
    try {
      const scale = Math.min(logoMaxW / logo.w, logoMaxH / logo.h);
      const drawW = logo.w * scale;
      const drawH = logo.h * scale;
      doc.addImage(logo.dataUri, logoPad, (headerH - drawH) / 2, drawW, drawH, undefined, 'FAST');
    } catch (_) { /* skip logo if addImage fails */ }
  }

  // Org name in header when there's no logo
  if (!logo) {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14); doc.setFont(undefined, 'bold');
    doc.text(iss.name || '', logoPad, headerH / 2 + 5);
  }

  // Invoice number + period (right side)
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13); doc.setFont(undefined, 'bold');
  doc.text(invNo, pageW - 28, 30, { align: 'right' });
  doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.setTextColor(148, 163, 184);
  doc.text(periodLabel, pageW - 28, 46, { align: 'right' });

  // --- From / Bill To ---
  let y = headerH + 32;
  doc.setTextColor(30); doc.setFont(undefined, 'bold'); doc.setFontSize(11);
  doc.text(iss.name || '', 56, y);
  doc.setFont(undefined, 'normal'); doc.setFontSize(9); doc.setTextColor(100);
  const fromLines = [
    iss.address_line1, iss.address_line2,
    ([iss.city, iss.state].filter(Boolean).join(', ') + (iss.zip ? ' ' + iss.zip : '')).trim() || null,
    iss.country,
  ].filter(Boolean);
  fromLines.forEach((l, i) => doc.text(String(l), 56, y + 14 + i * 12));

  doc.setTextColor(148, 163, 184); doc.setFontSize(8); doc.setFont(undefined, 'bold');
  doc.text('BILL TO', 360, y - 2);
  doc.setTextColor(30); doc.setFont(undefined, 'bold'); doc.setFontSize(11);
  doc.text(toName || '', 360, y + 13);

  y += 14 + Math.max(fromLines.length * 12, 26) + 28;

  // Divider
  doc.setDrawColor(226, 232, 240); doc.setLineWidth(1);
  doc.line(56, y, pageW - 56, y);
  y += 24;

  // --- Line items ---
  autoTable(doc, {
    startY: y,
    head: [['Client', 'Basis', 'Amount']],
    body: (stmt.line_items || []).map(l => [l.client_name, basisLabel(l), fmt$(l.line_total)]),
    foot: [['', 'Total', fmt$(stmt.total)]],
    theme: 'plain',
    headStyles: { fontStyle: 'bold', textColor: [148, 163, 184], fontSize: 8, cellPadding: { top: 4, bottom: 8, left: 0, right: 0 } },
    footStyles: { fontStyle: 'bold', textColor: [30, 30, 30], fontSize: 13, cellPadding: { top: 12, bottom: 4, left: 0, right: 0 } },
    styles: { fontSize: 10, cellPadding: { top: 8, bottom: 8, left: 0, right: 0 }, textColor: [40, 40, 40] },
    columnStyles: { 2: { halign: 'right' } },
    margin: { left: 56, right: 56 },
    didDrawCell: (data) => {
      if (data.section === 'body') {
        const { x, y: cy, width, height } = data.cell;
        doc.setDrawColor(241, 245, 249); doc.setLineWidth(0.5);
        doc.line(x, cy + height, x + width, cy + height);
      }
    },
  });

  const endY = (doc.lastAutoTable?.finalY || y) + 28;
  if (iss.payment_link) {
    doc.setTextColor(100); doc.setFontSize(9);
    doc.text('Pay online: ' + iss.payment_link, 56, endY);
  }

  const dataUri = doc.output('datauristring');
  return dataUri.substring(dataUri.indexOf(',') + 1);
}

// Convenience async wrapper: pre-fetches the logo then calls the sync builder.
export async function buildContractorInvoicePdfBase64Async(stmt, toName) {
  const iss = stmt.issuer_snapshot || {};
  const logo = iss.logo_url ? await fetchLogoForPdf(iss.logo_url) : null;
  return buildContractorInvoicePdfBase64(stmt, toName, logo);
}
